import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { History } from "lucide-react";
import { Table, Button, Form, Badge, Pagination } from "react-bootstrap";
import CreatePurchaseModal from "../components/CreatePurchaseModal";
import HubChangeLogModal from "../components/HubChangeLogModal";
import {
    purchaseDisplayDebtUAH,
    purchasePaymentStatus,
    purchaseRawDebtUAH,
    roundMoneyUAH,
    PURCHASE_DEBT_TOLERANCE_UAH,
    formatPurchaseQtyForList,
} from "../helpers/purchaseMoney";
import {
    purchasePaymentTypeLabel,
    purchaseUsesCashlessMarkup,
} from "../helpers/purchasePaymentType";

function sumPaidUAH(paymentsMap, purchaseId) {
    return (paymentsMap[purchaseId] || []).reduce((sum, pay) => sum + pay.amount / 100, 0);
}

/** Оплачено грн: з API (швидко) або з детального списку оплат після розгортання рядка. */
function paidUAHForPurchase(paymentsMap, purchase) {
    if (purchase?.paidUAH != null && Number.isFinite(Number(purchase.paidUAH))) {
        return Number(purchase.paidUAH);
    }
    return sumPaidUAH(paymentsMap, purchase._id);
}

function formatMoney2(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) {
        return "—";
    }
    const rounded = Math.round(n * 100) / 100;
    return rounded.toLocaleString("uk-UA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

/** Сума в грн → число для показу в обраній валюті (курс з перемикача або курс закупки). */
function uahToDisplayAmount(uahValue, displayCurrency, purchase, usdSellRate) {
    const u = Number(uahValue);
    if (!Number.isFinite(u)) {
        return null;
    }
    if (displayCurrency !== "USD") {
        return roundMoneyUAH(u);
    }
    let r = Number(usdSellRate);
    if (!Number.isFinite(r) || r <= 0) {
        r = Number(purchase?.exchangeRate);
    }
    if (!Number.isFinite(r) || r <= 0) {
        return roundMoneyUAH(u);
    }
    return Math.round((u / r) * 100) / 100;
}

function formatUahInDisplayCurrency(uahValue, displayCurrency, purchase, usdSellRate) {
    const n = uahToDisplayAmount(uahValue, displayCurrency, purchase, usdSellRate);
    return formatMoney2(n);
}

/** Канал оплати з прив’язаної транзакції (або вручну). */
function purchasePaymentTxChannelLabel(pay) {
    if (!pay?.transactionId) {
        return "Вручну";
    }
    const tx = pay.transactionId;
    if (typeof tx === "object" && tx != null && typeof tx.paymentType === "string") {
        return tx.paymentType === "cash" ? "Готівка" : "Безготівка";
    }
    return "Транзакція";
}

function purchaseFactPaymentParts(purchase) {
    const ch = purchase?.paymentByChannelUAH || {};
    const cash = roundMoneyUAH(Number(ch.cashUAH) || 0);
    const noncash = roundMoneyUAH(Number(ch.noncashUAH) || 0);
    const manual = roundMoneyUAH(Number(ch.manualUAH) || 0);
    const parts = [];
    if (cash > 0) {
        parts.push({ key: "cash", label: "Готівка", uah: cash });
    }
    if (noncash > 0) {
        parts.push({ key: "noncash", label: "Безготівка", uah: noncash });
    }
    if (manual > 0) {
        parts.push({ key: "manual", label: "Вручну", uah: manual });
    }
    return parts;
}

/** Рядок запиту як число (грн або $), якщо схоже на суму. */
function parsePurchaseAmountQuery(raw) {
    const t = String(raw || "")
        .trim()
        .replace(/\s/g, "")
        .replace(",", ".");
    if (!t || !/^\d+(\.\d{1,4})?$/.test(t)) {
        return null;
    }
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
}

/**
 * Пошук по постачальнику, рядках матеріалів, сумі (грн / у валюті таблиці), типу оплати.
 */
const PURCHASES_PAGE_SIZE_OPTIONS = [25, 50, 100];
const PURCHASES_PAGE_SIZE_LS = "purchasesListPageSize";
const PURCHASE_PAYMENT_COMMENT_MAX = 500;

function readSavedPurchasesPageSize() {
    try {
        const raw = localStorage.getItem(PURCHASES_PAGE_SIZE_LS);
        const n = Number.parseInt(String(raw), 10);
        if (PURCHASES_PAGE_SIZE_OPTIONS.includes(n)) {
            return n;
        }
    } catch {
        /* ignore */
    }
    return 50;
}

async function fetchPurchasesListPage(pageNum, pageSize) {
    const res = await axios.get("/api/purchases", {
        params: { page: pageNum, pageSize },
    });
    return res.data;
}

function dedupeTransactionsById(list) {
    if (!Array.isArray(list)) {
        return [];
    }
    const map = new Map();
    for (const t of list) {
        if (t && t._id != null) {
            map.set(String(t._id), t);
        }
    }
    return [...map.values()];
}

function purchaseMatchesFilter(p, qRaw, displayCurrency, usdSellRate) {
    const qTrim = String(qRaw || "").trim();
    if (!qTrim) {
        return true;
    }
    const q = qTrim.toLowerCase();

    const parts = [];
    parts.push(p.supplierId?.name || "Разова закупка");
    (p.materialLines || []).forEach((line) => parts.push(line));
    if (p.category) {
        parts.push(p.category);
    }

    const pt = p.paymentType;
    parts.push(purchasePaymentTypeLabel(pt));
    if (pt) {
        parts.push(pt);
    }
    purchaseFactPaymentParts(p).forEach((x) => {
        parts.push(x.label, String(x.uah), formatMoney2(x.uah));
    });

    if (pt === "cash") {
        parts.push("готівка", "нал", "cash");
    }
    if (pt === "card" || pt === "noncash" || pt === "fop_requisites") {
        parts.push("безнал", "безготівка", "безготівков", "картка", "реквізити", "фоп", "noncash");
    }

    const totalUah = roundMoneyUAH(Number(p.totalUAH) || 0);
    parts.push(String(totalUah), formatMoney2(totalUah));

    const disp = uahToDisplayAmount(totalUah, displayCurrency, p, usdSellRate);
    if (disp != null && Number.isFinite(disp)) {
        parts.push(String(disp), formatMoney2(disp));
    }

    const paidUah = roundMoneyUAH(Number(p.paidUAH) || 0);
    if (paidUah > 0) {
        parts.push(String(paidUah), formatMoney2(paidUah), "оплачено", "оплата");
        const paidDisp = uahToDisplayAmount(paidUah, displayCurrency, p, usdSellRate);
        if (paidDisp != null && Number.isFinite(paidDisp)) {
            parts.push(String(paidDisp), formatMoney2(paidDisp));
        }
    }

    const blob = parts.join(" ").toLowerCase();
    if (blob.includes(q)) {
        return true;
    }

    const qNum = parsePurchaseAmountQuery(qTrim);
    if (qNum != null) {
        if (Math.abs(totalUah - qNum) < 0.02) {
            return true;
        }
        if (disp != null && Number.isFinite(disp) && Math.abs(disp - qNum) < 0.02) {
            return true;
        }
        if (paidUah > 0) {
            if (Math.abs(paidUah - qNum) < 0.02) {
                return true;
            }
            const paidDisp = uahToDisplayAmount(paidUah, displayCurrency, p, usdSellRate);
            if (
                paidDisp != null &&
                Number.isFinite(paidDisp) &&
                Math.abs(paidDisp - qNum) < 0.02
            ) {
                return true;
            }
        }
        for (const x of purchaseFactPaymentParts(p)) {
            if (Math.abs(x.uah - qNum) < 0.02) {
                return true;
            }
            const chDisp = uahToDisplayAmount(x.uah, displayCurrency, p, usdSellRate);
            if (
                chDisp != null &&
                Number.isFinite(chDisp) &&
                Math.abs(chDisp - qNum) < 0.02
            ) {
                return true;
            }
        }
    }

    return false;
}

export default function PurchasesPage({ displayCurrency = "UAH", usdSellRate = null }) {

    const [purchases, setPurchases] = useState([]);
    const [showCreate,setShowCreate] = useState(false);
    const [editId,setEditId] = useState(null);
    const [expanded,setExpanded] = useState(null);
    const [items,setItems] = useState({});
    const [payments, setPayments] = useState({});
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [selectedTx, setSelectedTx] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState("");
    const [paymentComment, setPaymentComment] = useState("");
    const [loading, setLoading] = useState(false);
    const [paymentTxDateFrom, setPaymentTxDateFrom] = useState("");
    const [paymentTxDateTo, setPaymentTxDateTo] = useState("");
    const [loadingPaymentTx, setLoadingPaymentTx] = useState(false);
    const [showDebug, setShowDebug] = useState(false);
    const [debugTxs, setDebugTxs] = useState([]);
    const [paymentsList, setPaymentsList] = useState([]);
    const [filterQ, setFilterQ] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(readSavedPurchasesPageSize);
    const [purchasesTotal, setPurchasesTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [showChangeLog, setShowChangeLog] = useState(false);

    const handlePageSizeChange = (e) => {
        const next = Number.parseInt(String(e.target.value), 10);
        if (!PURCHASES_PAGE_SIZE_OPTIONS.includes(next)) {
            return;
        }
        try {
            localStorage.setItem(PURCHASES_PAGE_SIZE_LS, String(next));
        } catch {
            /* ignore */
        }
        setPageSize(next);
        setPage(1);
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const d = await fetchPurchasesListPage(page, pageSize);
                if (cancelled) {
                    return;
                }
                setPurchases(d.items);
                setPurchasesTotal(d.total);
                setTotalPages(d.totalPages);
                setPage(d.page);
            } catch (e) {
                console.error(e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [page, pageSize]);

    useEffect(() => {
        setExpanded(null);
    }, [page]);

    useEffect(() => {
        if (selectedTx && selectedTx.available != null) {
            setPaymentAmount((selectedTx.available / 100).toFixed(2));
        }
    }, [selectedTx]);

    const reloadPurchasesFirstPage = async () => {
        const d = await fetchPurchasesListPage(1, pageSize);
        setPurchases(d.items);
        setPurchasesTotal(d.total);
        setTotalPages(d.totalPages);
        setPage(d.page);
    };

    const refreshPurchasesCurrentPage = async (pageNum = page) => {
        const d = await fetchPurchasesListPage(pageNum, pageSize);
        setPurchases(d.items);
        setPurchasesTotal(d.total);
        setTotalPages(d.totalPages);
        setPage(d.page);
        return d;
    };

    const editPurchase = async(id)=>{

        const res = await axios.get(`/api/purchases/${id}`);

        setEditId(id);

        setShowCreate(true);

    };

    const updateItemComment = async (itemId, value) => {
        try {
            await axios.put(`/api/purchase-items/${itemId}`, {
                comment: value
            });
        } catch (err) {
            console.error(err);
        }
    };

    const addPayment = async (purchaseId) => {

        const amount = prompt("Сума оплати");

        if (!amount) return;

        await axios.post("/api/purchase-payments", {
            purchaseId,
            amount: Number(amount)
        });

        // обновляем оплаты
        const resPayments = await axios.get(`/api/purchase-payments/${purchaseId}`);

        setPayments(prev => ({
            ...prev,
            [purchaseId]: resPayments.data
        }));

        await refreshPurchasesCurrentPage();
    };

    const togglePurchase = async(id)=>{

        if(expanded === id){

            setExpanded(null);
            return;

        }

        if (payments[id] === undefined) {
            const resPayments = await axios.get(`/api/purchase-payments/${id}`);

            setPayments(prev => ({
                ...prev,
                [id]: resPayments.data
            }));
        }

        if(!items[id]){

            const res = await axios.get(`/api/purchases/${id}/items`);

            setItems({
                ...items,
                [id]:res.data
            });

        }

        setExpanded(id);

    };

    const deletePurchase = async(id)=>{

        if(!window.confirm("Видалити закупку?")) return;

        await axios.delete(`/api/purchases/${id}`);

        setExpanded(null);
        const d = await fetchPurchasesListPage(page, pageSize);
        if (d.items.length === 0 && page > 1) {
            setPage(page - 1);
        } else {
            setPurchases(d.items);
            setPurchasesTotal(d.total);
            setTotalPages(d.totalPages);
            setPage(d.page);
        }

    };

    const defaultPaymentTxDateRange = () => {
        const to = new Date();
        const from = new Date();
        from.setDate(from.getDate() - 365);
        return {
            dateFrom: from.toISOString().slice(0, 10),
            dateTo: to.toISOString().slice(0, 10)
        };
    };

    const fetchPaymentTransactions = async (
        purchase,
        dateFrom,
        dateTo,
        { paidUAHOverride } = {}
    ) => {
        const paid =
            paidUAHOverride != null && Number.isFinite(Number(paidUAHOverride))
                ? Number(paidUAHOverride)
                : paidUAHForPurchase(payments, purchase);
        const rawDebt = purchaseRawDebtUAH(purchase.totalUAH, paid);
        const debtForApi = rawDebt > 0 ? rawDebt : 0.01;

        const params = new URLSearchParams({
            mode: "payments",
            debt: String(debtForApi),
            dateFrom,
            dateTo
        });

        const res = await axios.get(`/api/transactions?${params.toString()}`);

        return dedupeTransactionsById(res.data);
    };

    const autoSelectMatchingTx = (txs, purchase) => {
        const paid = paidUAHForPurchase(payments, purchase);
        const debt = purchaseRawDebtUAH(purchase.totalUAH, paid);
        if (debt <= 0) return;
        const match = txs.find(t => Math.abs(t.available / 100 - debt) < 0.01);
        if (match) {
            setSelectedTx(match);
            setPaymentAmount((match.available / 100).toFixed(2));
        }
    };

    const openPaymentModal = async (purchase) => {
        const { dateFrom, dateTo } = defaultPaymentTxDateRange();
        setPaymentTxDateFrom(dateFrom);
        setPaymentTxDateTo(dateTo);
        setSelectedTx(null);
        setPaymentAmount("");
        setPaymentComment("");
        setLoadingPaymentTx(true);
        try {
            const txs = await fetchPaymentTransactions(purchase, dateFrom, dateTo);
            setTransactions(txs);
            autoSelectMatchingTx(txs, purchase);
        } finally {
            setLoadingPaymentTx(false);
        }
        setSelectedPurchase(purchase);
        setShowPaymentModal(true);
    };

    const refreshPaymentTransactions = async (purchaseOverride, fetchOpts) => {
        const purchase = purchaseOverride ?? selectedPurchase;
        if (!purchase || !paymentTxDateFrom || !paymentTxDateTo) {
            return;
        }
        setLoadingPaymentTx(true);
        try {
            const txs = await fetchPaymentTransactions(
                purchase,
                paymentTxDateFrom,
                paymentTxDateTo,
                fetchOpts || {}
            );
            setTransactions(txs);
            setSelectedTx(null);
            setPaymentAmount("");
            setPaymentComment("");
            autoSelectMatchingTx(txs, purchase);
        } finally {
            setLoadingPaymentTx(false);
        }
    };

    const closePaymentModal = () => {
        setShowPaymentModal(false);
        setSelectedTx(null);
        setPaymentAmount("");
        setPaymentComment("");
    };

    const submitPayment = async () => {


        if (!selectedTx || !paymentAmount) {
            alert("Заповніть дані");
            return;
        }

        const amt = Number(paymentAmount);
        if (!Number.isFinite(amt) || amt <= 0) {
            alert("Некоректна сума");
            return;
        }

        if (Math.round(amt * 100) > selectedTx.available) {
            alert("Сума більша ніж доступно");
            return;
        }

        const paid = paidUAHForPurchase(payments, selectedPurchase);
        const rawDebt = purchaseRawDebtUAH(selectedPurchase.totalUAH, paid);

        /* Один банківський платіж може закривати борг по закупці й «зайве» (інші товари в чеку без рядка в закупці). */
        if (amt > rawDebt + 1e-6) {
            const over = roundMoneyUAH(amt - rawDebt);
            const ok = window.confirm(
                `Сума прив’язки ${formatMoney2(amt)} грн більша за поточний борг ${formatMoney2(
                    rawDebt
                )} грн (різниця ≈ ${formatMoney2(
                    over
                )} грн).\n\n` +
                    `Так буває, якщо в одному переказі оплачено й те, що є в закупці, й інше (наприклад, дрібниці для цеху), яке ви не заносили в документ.\n\n` +
                    `Закупка відобразиться як оплачена; зайве не створює «борг постачальника до вас» в цій таблиці. Продовжити?`
            );
            if (!ok) {
                return;
            }
        }
        setLoading(true);
        try {
            const commentTrim = paymentComment.trim().slice(0, PURCHASE_PAYMENT_COMMENT_MAX);
            await axios.post("/api/purchase-payments", {
                purchaseId: selectedPurchase._id,
                transactionId: selectedTx._id,
                amount: Math.round(amt * 100),
                ...(commentTrim ? { comment: commentTrim } : {}),
            });

            const listData = await refreshPurchasesCurrentPage(page);
            const freshPurchase = listData.items.find(
                (x) => String(x._id) === String(selectedPurchase._id)
            );
            if (freshPurchase) {
                setSelectedPurchase(freshPurchase);
            }

            const resPayments = await axios.get(
                `/api/purchase-payments/${selectedPurchase._id}`
            );
            setPayments((prev) => ({
                ...prev,
                [selectedPurchase._id]: resPayments.data
            }));

            const paidAfter = resPayments.data.reduce(
                (sum, pay) => sum + pay.amount / 100,
                0
            );
            const totalForDebt = (freshPurchase || selectedPurchase).totalUAH;
            const displayDebt = purchaseDisplayDebtUAH(totalForDebt, paidAfter);

            setSelectedTx(null);
            setPaymentAmount("");
            setPaymentComment("");

            if (displayDebt <= 0) {
                setShowPaymentModal(false);
            } else {
                await refreshPaymentTransactions(freshPurchase || selectedPurchase, {
                    paidUAHOverride: paidAfter,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const formatTxTime = (time) => {
        const date = new Date(time);

        const months = [
            "січ.", "лют.", "бер.", "квіт.", "трав.", "черв.",
            "лип.", "серп.", "вер.", "жовт.", "лист.", "груд."
        ];

        const day = date.getDate();
        const month = months[date.getMonth()];
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");

        return `${day} ${month} ${hours}:${minutes}`;
    };

    const filteredPurchases = useMemo(
        () =>
            purchases.filter((p) =>
                purchaseMatchesFilter(p, filterQ, displayCurrency, usdSellRate)
            ),
        [purchases, filterQ, displayCurrency, usdSellRate]
    );

    return (

        <div className="production-tab-panel tab-content">

            <div className="mb-3">
                <h5 className="mb-1">Закупки</h5>
                <p className="text-muted small mb-2">
                    <strong>Закупівля у постачальника</strong> — кнопка «+» відкриває форму з документом
                    закупівлі, рухом складу та обліком боргу. Залишок до{" "}
                    {PURCHASE_DEBT_TOLERANCE_UAH} грн після оплат показується як 0 і статус «Оплачено».
                </p>

                <Form.Label htmlFor="purchases-filter-q" className="visually-hidden">
                    Пошук закупок
                </Form.Label>
                <div
                    className="d-flex flex-nowrap align-items-stretch gap-2"
                    style={{ minWidth: 0 }}
                >
                    <Form.Control
                        id="purchases-filter-q"
                        className="flex-grow-1"
                        style={{ minWidth: 0 }}
                        value={filterQ}
                        onChange={(e) => setFilterQ(e.target.value)}
                        placeholder="Постачальник, матеріал, сума, тип оплати… (поточна сторінка)"
                        aria-label="Пошук закупок"
                    />
                    <Button
                        type="button"
                        variant="outline-secondary"
                        className="px-2 flex-shrink-0 align-self-stretch d-flex align-items-center"
                        onClick={() => setShowChangeLog(true)}
                        title="Історія змін закупок"
                        aria-label="Історія змін закупок"
                    >
                        <History size={18} aria-hidden />
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        className="px-3 flex-shrink-0 align-self-stretch"
                        onClick={() => setShowCreate(true)}
                        title="Нова закупка (закупівля у постачальника)"
                        aria-label="Нова закупка (закупівля у постачальника)"
                    >
                        +
                    </Button>
                </div>
            </div>

            <div className="border rounded purchases-list-wrap">
                <Table bordered hover className="materials-hub-table align-middle mb-0">
                <thead className="table-dark text-center">
                <tr>
                    <th>Дата</th>
                    <th>Постачальник</th>
                    <th className="text-nowrap">Оплата</th>
                    <th>
                        {displayCurrency === "USD" ? "Сума ($)" : "Сума (₴)"}
                    </th>
                    <th>Статус</th>
                    <th className="text-nowrap">Дії</th>
                </tr>
                </thead>

                <tbody>
                {filteredPurchases.length === 0 ? (
                    <tr>
                        <td colSpan={6} className="text-muted text-center py-4">
                            {purchases.length === 0
                                ? "Немає закупок."
                                : "Нічого не знайдено за запитом."}
                        </td>
                    </tr>
                ) : (
                    filteredPurchases.map((p) => {
                    const paid = paidUAHForPurchase(payments, p);
                    const payStatus = purchasePaymentStatus(p.totalUAH, paid);
                    const factPaymentParts = purchaseFactPaymentParts(p);

                    return <React.Fragment key={p._id}>
                        <tr
                            key={p._id}
                            className={expanded === p._id ? "purchase-row active" : "purchase-row"}
                            style={{cursor: "pointer"}}
                            onClick={() => togglePurchase(p._id)}
                        >
                            <td>
                                {expanded === p._id ? "▼" : "▶"} {new Date(p.date).toLocaleDateString()}
                            </td>

                            <td className="align-top">
                                <div>
                                    {p.supplierId?.name || "Разова закупка"}
                                </div>
                                {Array.isArray(p.materialLines) && p.materialLines.length > 0 && (
                                    <ul className="purchase-material-lines mb-0 mt-1 ps-3">
                                        {p.materialLines.map((line, idx) => (
                                            <li key={idx}>{line}</li>
                                        ))}
                                    </ul>
                                )}
                            </td>

                            <td className="small">
                                {payStatus === "debt" ? null : factPaymentParts.length > 0 ? (
                                    <ul className="purchase-payment-channels-list mb-0 ps-3">
                                        {factPaymentParts.map((seg) => (
                                            <li key={seg.key}>
                                                {factPaymentParts.length === 1 ? (
                                                    seg.label
                                                ) : (
                                                    <>
                                                        <span className="text-muted">{seg.label}:</span>{" "}
                                                        <span className="text-nowrap">
                                                            {formatUahInDisplayCurrency(
                                                                seg.uah,
                                                                displayCurrency,
                                                                p,
                                                                usdSellRate
                                                            )}{" "}
                                                            {displayCurrency === "USD" ? "USD" : "грн"}
                                                        </span>
                                                    </>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <>
                                        {purchasePaymentTypeLabel(p.paymentType)}
                                        {purchaseUsesCashlessMarkup(p.paymentType) &&
                                        Number(p.cashlessPercent) > 0 ? (
                                            <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                                                +{p.cashlessPercent}%
                                            </div>
                                        ) : null}
                                    </>
                                )}
                            </td>

                            <td>
                                {formatUahInDisplayCurrency(
                                    p.totalUAH,
                                    displayCurrency,
                                    p,
                                    usdSellRate
                                )}
                            </td>

                            <td>
                                {payStatus === "debt" && (
                                    <span style={{ color: "red" }}>Борг</span>
                                )}
                                {payStatus === "partial" && (
                                    <span style={{ color: "orange" }}>Частково</span>
                                )}
                                {payStatus === "paid" && (
                                    <span style={{ color: "green" }}>Оплачено</span>
                                )}
                            </td>

                            <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                <div className="d-inline-flex gap-2 justify-content-center align-items-center flex-wrap">
                                    <Button
                                        size="sm"
                                        variant="success-outline"
                                        onClick={() => openPaymentModal(p)}
                                        aria-label="Оплата"
                                    >
                                        💰
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="warning-outline"
                                        onClick={() => editPurchase(p._id)}
                                        aria-label="Редагувати"
                                    >
                                        ✏️
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="danger-outline"
                                        onClick={() => deletePurchase(p._id)}
                                        aria-label="Видалити"
                                    >
                                        🗑
                                    </Button>
                                </div>
                            </td>

                        </tr>
                        {expanded === p._id && (

                            <tr className="purchase-expanded">

                                <td colSpan={6}>
                                    {/* 💰 ОПЛАТИ */}
                                    <div
                                        className="d-flex flex-wrap align-items-center gap-2"
                                        style={{ marginBottom: 15, textAlign: "left" }}
                                    >
                                        {(payments[p._id] || []).length === 0 ? (
                                            <span style={{ color: "#999", fontSize: 13 }} className="ps-3">
                                                Немає оплат
                                            </span>
                                        ) : (
                                            <>
                                                <b className="me-1">Оплати:</b>
                                                {(payments[p._id] || []).map((pay) => (
                                                    <div
                                                        key={pay._id}
                                                        className="ps-2 d-inline-flex flex-column align-items-start"
                                                        style={{
                                                            padding: "4px 0",
                                                            gap: "0.2rem",
                                                            maxWidth: "min(100%, 22rem)",
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: "0.35rem",
                                                            }}
                                                        >
                                                            <Badge
                                                                bg="secondary"
                                                                className="fw-normal text-wrap"
                                                                style={{ maxWidth: "7.5rem" }}
                                                            >
                                                                {purchasePaymentTxChannelLabel(pay)}
                                                            </Badge>
                                                            <span>
                                                                {formatUahInDisplayCurrency(
                                                                    pay.amount / 100,
                                                                    displayCurrency,
                                                                    p,
                                                                    usdSellRate
                                                                )}{" "}
                                                                {displayCurrency === "USD" ? "USD" : "грн"}
                                                            </span>

                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                className=" border-0"
                                                                onClick={async () => {
                                                                    await axios.delete(
                                                                        `/api/purchase-payments/${pay._id}`
                                                                    );

                                                                    await refreshPurchasesCurrentPage();
                                                                    const resPay = await axios.get(
                                                                        `/api/purchase-payments/${p._id}`
                                                                    );
                                                                    setPayments((prev) => ({
                                                                        ...prev,
                                                                        [p._id]: resPay.data,
                                                                    }));
                                                                }}
                                                            >
                                                                ❌
                                                            </Button>
                                                        </span>
                                                        {pay.comment ? (
                                                            <span
                                                                className="small text-muted text-break"
                                                                style={{ paddingLeft: "0.1rem" }}
                                                            >
                                                                {pay.comment}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                ))}
                                                {payStatus === "partial" ? (
                                                    <span className="ps-2 ms-1 border-start text-nowrap">
                                                        <span className="text-muted">Залишок:</span>{" "}
                                                        <strong style={{ color: "#fd7e14" }}>
                                                            {formatUahInDisplayCurrency(
                                                                purchaseRawDebtUAH(p.totalUAH, paid),
                                                                displayCurrency,
                                                                p,
                                                                usdSellRate
                                                            )}{" "}
                                                            {displayCurrency === "USD" ? "USD" : "грн"}
                                                        </strong>
                                                    </span>
                                                ) : null}
                                            </>
                                        )}
                                    </div>

                                    <div className="materials-hub-table-scroll">
                                        <Table size="sm" bordered className="materials-hub-table mb-0">
                                        <thead className="table-secondary text-center">
                                        <tr>
                                            <th>Матеріал</th>
                                            <th>Колір</th>
                                            <th>Кількість</th>
                                            <th>Ціна $</th>
                                            <th>Ціна грн</th>
                                            <th>Коментар</th>
                                        </tr>
                                        </thead>

                                        <tbody>

                                        {items[p._id]?.map((i) => (
                                            <tr key={i._id}>
                                                <td>{i.materialId?.typeId?.name}</td>
                                                <td>{i.materialId?.color}</td>
                                                <td>
                                                    {Array.isArray(i.rollAmounts) && i.rollAmounts.length > 0 ? (
                                                        <div>
                                                            <strong>{formatPurchaseQtyForList(i.qty)}</strong>{" "}
                                                            {i.unit}
                                                            <span className="text-muted small ms-1">
                                                                ({i.rollAmounts.length} рул.:{" "}
                                                                {i.rollAmounts
                                                                    .map((r) => formatPurchaseQtyForList(r))
                                                                    .join(" + ")})
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {formatPurchaseQtyForList(i.qty)} {i.unit}
                                                        </>
                                                    )}
                                                </td>
                                                <td>{formatMoney2(i.priceUSD)}</td>
                                                <td>{formatMoney2(i.priceUAH)}</td>
                                                <td>
                                                    <Form.Control
                                                        size="sm"
                                                        placeholder="Коментар..."
                                                        value={i.comment || ""}

                                                        onChange={(e) => {
                                                            const val = e.target.value;

                                                            // обновляем локально (чтобы сразу отображалось)
                                                            const updated = {...items};

                                                            updated[p._id] = updated[p._id].map(x =>
                                                                x._id === i._id ? {...x, comment: val} : x
                                                            );

                                                            setItems(updated);
                                                        }}

                                                        onBlur={(e) => {
                                                            updateItemComment(i._id, e.target.value);
                                                        }}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                        </Table>
                                    </div>
                                </td>
                            </tr>

                        )}
                    </React.Fragment>

                    })
                )}

                </tbody>

                </Table>

                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-2 py-1 border-top bg-body-secondary">
                    <small className="text-muted mb-0 text-truncate min-w-0">
                        {purchasesTotal === 0
                            ? "0 закупок"
                            : `Усього ${purchasesTotal} · сторінка ${page} з ${totalPages}`}
                    </small>
                    <div className="d-flex flex-wrap align-items-center gap-2 flex-shrink-0 ms-auto">
                        <div className="d-flex align-items-center gap-1">
                            <Form.Label
                                htmlFor="purchases-page-size"
                                className="small text-muted mb-0 text-nowrap"
                            >
                                На сторінці
                            </Form.Label>
                            <Form.Select
                                id="purchases-page-size"
                                size="sm"
                                className="w-auto"
                                value={pageSize}
                                onChange={handlePageSizeChange}
                                aria-label="Кількість закупок на сторінці"
                            >
                                {PURCHASES_PAGE_SIZE_OPTIONS.map((n) => (
                                    <option key={n} value={n}>
                                        {n}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    <Pagination size="sm" className="mb-0 flex-nowrap gap-2 purchases-pagination-gap">
                        <Pagination.First
                            disabled={page <= 1}
                            onClick={() => setPage(1)}
                        />
                        <Pagination.Prev
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        />
                        <Pagination.Item active>{page}</Pagination.Item>
                        <Pagination.Next
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        />
                        <Pagination.Last
                            disabled={page >= totalPages}
                            onClick={() => setPage(totalPages)}
                        />
                    </Pagination>
                    </div>
                </div>
            </div>
            <CreatePurchaseModal
                show={showCreate}
                onHide={() => {
                    setShowCreate(false);
                    setEditId(null);
                }}
                reload={reloadPurchasesFirstPage}
                editId={editId}
            />
            {showPaymentModal && (
                <div className="modal d-block" style={{background:"rgba(0,0,0,0.5)"}}>
                    <div className="modal-dialog">
                        <div className="modal-content p-3">

                            <h5>Прив’язати оплату</h5>

                            <div style={{marginBottom: 10}}>
                                Борг:{" "}
                                {formatUahInDisplayCurrency(
                                    purchaseDisplayDebtUAH(
                                        selectedPurchase.totalUAH,
                                        sumPaidUAH(payments, selectedPurchase._id)
                                    ),
                                    displayCurrency,
                                    selectedPurchase,
                                    usdSellRate
                                )}{" "}
                                {displayCurrency === "USD" ? "USD" : "грн"}
                            </div>

                            <p className="text-muted small mb-2">
                                Банківські витрати з категоріями: закупка тканини/фурнітури, без категорії,
                                а також <strong>Робота з тканиною</strong>, <strong>Пакування</strong>,{" "}
                                <strong>Виробництво</strong> — за обраний період (до 500 дн.). У списку лише
                                операції з <strong>невикористаним залишком</strong> (після прив’язки сума
                                оновлюється). Якщо платежу немає — розширте дати або перевірте імпорт з банку.
                                Якщо в одному переказі оплачено й товар з закупки, й дрібниці поза нею — можна
                                прив’язати <strong>повну суму платежу</strong> (запитаємо підтвердження) або лише
                                суму боргу — тоді залишок на транзакції лишиться вільним.
                            </p>

                            <div className="d-flex flex-wrap align-items-end gap-2 mb-2">
                                <Form.Group className="mb-0">
                                    <Form.Label className="small mb-0">З дати</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={paymentTxDateFrom}
                                        onChange={(e) => setPaymentTxDateFrom(e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-0">
                                    <Form.Label className="small mb-0">По дату</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={paymentTxDateTo}
                                        onChange={(e) => setPaymentTxDateTo(e.target.value)}
                                    />
                                </Form.Group>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="mb-1"
                                    disabled={loadingPaymentTx}
                                    onClick={() => refreshPaymentTransactions()}
                                >
                                    {loadingPaymentTx ? "Завантаження…" : "Оновити список"}
                                </Button>
                            </div>

                            <div style={{ maxHeight: 300, overflow: "auto" }}>
                                {transactions.length === 0 && !loadingPaymentTx && (
                                    <div className="text-muted small py-2">
                                        Немає доступних витрат за цей період (з невикористаним залишком).
                                    </div>
                                )}
                                {transactions.map(t => (
                                    <div
                                        key={t._id}
                                        onClick={() => {
                                            setSelectedTx(t);
                                            setPaymentAmount((t.available / 100).toFixed(2));
                                        }}
                                        style={{
                                            padding: 10,
                                            border: selectedTx?._id === t._id
                                                ? "1px solid #0d6efd"
                                                : "1px solid #eee",
                                            background: selectedTx?._id === t._id
                                                ? "#e7f1ff"
                                                : "#fff",
                                            marginBottom: 6,
                                            cursor: "pointer",
                                            borderRadius: 10,
                                            transition: "all 0.2s"
                                        }}
                                    >
                                        <div style={{ fontWeight: 600 }}>
                                            {formatMoney2(t.available / 100)} грн
                                        </div>

                                        <div style={{ fontSize: 12, color: "#666" }}>
                                            {formatTxTime(t.time)} • {t.sourceKey} • {t.paymentType}
                                            {t.category ? ` • ${t.category}` : ""}
                                        </div>
                                        {t.description ? (
                                            <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                                                {t.description}
                                            </div>
                                        ) : null}
                                        {selectedTx?._id === t._id && (
                                            <div style={{ color: "#0d6efd", fontWeight: 700 }}>
                                                ✓
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <Form.Control
                                placeholder="Сума"
                                value={paymentAmount}
                                onChange={(e)=>{
                                    let val = Number(e.target.value);

                                    if (selectedTx && Math.round(val * 100) > selectedTx.available) {
                                        val = Math.round((selectedTx.available / 100) * 100) / 100;
                                    }

                                    setPaymentAmount(val);
                                }}
                                onBlur={() => {
                                    if (paymentAmount === "" || !selectedTx) return;
                                    const v = Number(paymentAmount);
                                    if (!Number.isFinite(v)) return;
                                    const max = selectedTx.available / 100;
                                    const clamped = Math.min(v, max);
                                    setPaymentAmount(String(Math.round(clamped * 100) / 100));
                                }}
                            />

                            <Form.Group className="mt-2 mb-0">
                                <Form.Label className="small text-muted mb-1">
                                    Коментар до оплати (необов’язково)
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    placeholder="За потреби — коротке пояснення до цієї оплати"
                                    value={paymentComment}
                                    onChange={(e) =>
                                        setPaymentComment(
                                            e.target.value.slice(0, PURCHASE_PAYMENT_COMMENT_MAX)
                                        )
                                    }
                                    disabled={loading}
                                    aria-label="Коментар до прив’язки оплати"
                                />
                            </Form.Group>

                            <div className="d-flex justify-content-end gap-2 mt-3">

                                <Button
                                    variant="secondary"
                                    onClick={closePaymentModal}
                                >
                                    Скасувати
                                </Button>
                                <Button
                                    disabled={loading}
                                    onClick={submitPayment}
                                >
                                    {loading ? "Збереження..." : "Зберегти"}
                                </Button>
                            </div>

                        </div>
                    </div>
                </div>
            )}
            {showDebug && (
                <div className="modal d-block" style={{background:"rgba(0,0,0,0.5)"}}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content p-3">

                            <h6 className="mt-3">Оплати</h6>

                            {paymentsList.map(p => (
                                <div
                                    key={p._id}
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        padding: 6,
                                        borderBottom: "1px solid #eee"
                                    }}
                                >
                                    <div>
                                        {formatMoney2(p.amount / 100)} грн
                                        <div style={{ fontSize: 12, color: "#666" }}>
                                            purchase: {p.purchaseId}
                                        </div>
                                    </div>

                                    <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={async () => {
                                            await axios.delete(`/api/purchase-payments/${p._id}`);

                                            // обновляем локально
                                            setPaymentsList(prev =>
                                                prev.filter(x => x._id !== p._id)
                                            );

                                            await refreshPurchasesCurrentPage();

                                            // 🔥 обновляем usedAmount список
                                            const txRes = await axios.get("/api/transactions/debug/used");
                                            setDebugTxs(txRes.data);
                                        }}
                                    >
                                        ❌
                                    </Button>
                                </div>
                            ))}

                            <div className="text-end mt-3">
                                <Button onClick={()=>setShowDebug(false)}>
                                    Закрити
                                </Button>
                            </div>

                        </div>
                    </div>
                </div>
            )}

            <HubChangeLogModal
                show={showChangeLog}
                onHide={() => setShowChangeLog(false)}
                scope="purchase"
                title="Історія змін — закупки"
            />
        </div>

    );
}